/* eslint-disable import/order */
import type { Formula } from "@intergamma/common/formula";
import Cookies from "js-cookie";
import * as config from "./config";
import { ExperimentName, VariantName } from "./types";
import { ExperimentDomainAndIndex } from "./types/config";
import { coinFlip } from "./coin-flip";

type Options = {
  dev: boolean;
  formula: Formula;
  onCoinflip<E extends ExperimentName>(
    name: E,
    experiment: (typeof config.experiments)[E],
    variant: keyof (typeof config.experiments)[E]["variants"],
  ): void;
};

type ExperimentMap = Record<
  string,
  {
    domain_and_index: ExperimentDomainAndIndex;
    variant: string;
  }
>;
export class Experiments {
  #dev: Options["dev"] = false;
  #formula?: Options["formula"];
  #onCoinflip?: Options["onCoinflip"];

  public configure(options: Options) {
    this.#dev = options.dev;
    this.#formula = options.formula;
    this.#onCoinflip = options.onCoinflip;
  }

  /**
   * Get active experiments with their chosen variant.
   */
  get active() {
    return Object.entries(config.experiments).reduce<ExperimentMap>(
      (acc, [name, experiment]) => {
        const variant = Cookies.get(name) ?? "";

        if (variant in experiment.variants) {
          return {
            ...acc,
            [name]: {
              domain_and_index: experiment.domain_and_index,
              variant,
            },
          };
        }

        return acc;
      },
      {},
    );
  }

  public getVariant<T extends ExperimentName>(experimentName: T) {
    const experiment = config.experiments[experimentName];
    const cookie = Cookies.get(experimentName) ?? "";

    if (!experiment) {
      throw Error(
        `Could not find experiment "${experimentName}". Make sure to run \`pnpm run build:packages\` if this is a new experiment.`,
      );
    }

    // Ensure experiment is enabled for current formula
    if (this.#formula && !experiment.formula[this.#formula.id]) {
      return "A-control" as const;
    }

    return Object.keys(experiment.variants).includes(cookie)
      ? (cookie as VariantName<T>)
      : undefined;
  }

  public setVariant<T extends ExperimentName>(
    experimentName: T,
    variant: VariantName<T>,
  ) {
    if (!this.#formula) {
      return;
    }

    const domain =
      document.location.hostname === "localhost"
        ? document.location.hostname
        : this.#formula.baseDomain;

    Cookies.set(experimentName, variant as string, {
      expires: 35,
      domain,
      sameSite: "strict",
    });

    this.#onCoinflip?.(
      experimentName,
      config.experiments[experimentName],
      variant,
    );
  }

  public coinFlip<T extends ExperimentName>(experimentName: T) {
    const experiment = config.experiments[experimentName];
    const variant = coinFlip(experiment.variants, this.#dev);

    if (variant) return variant as VariantName<T>;

    throw new Error(`Failed choosing random variant for "${experimentName}"`);
  }
}
